import Loader from "@/shared/components/ui/Loaders/Loader";
import { useMainStore } from "@/stores/index";
import { useEffect, useState } from "react";
import { showSuccess, showError } from "@/shared/utils/toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getTargetRoute } from "@/shared/utils/helperFunctions";

const AuthSuccessHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useMainStore();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const processAuthSuccess = async () => {
      try {
        setIsProcessing(true);
        setError(null);

        // Extract ALL URL parameters
        const allParams = {};
        searchParams.forEach((value, key) => {
          allParams[key] = value;
        });

        // Extract specific parameters
        const success = searchParams.get("success");
        const token = searchParams.get("token");
        const onboardFlag = searchParams.get("onboardFlag");
        const code = searchParams.get("code");
        const hasAccessRequest =
          searchParams.get("access_request_sent") === "true" ||
          searchParams.get("hasAccessRequest") === "true";
        const requestTarget = searchParams.get("request_target");
        const orgName = searchParams.get("org_name");
        const requestSubmittedAt = searchParams.get("request_submitted_at");
        const approvalStatusParam = searchParams.get("approval_status");
        const rejectedBy = searchParams.get("rejected_by");
        const errorParam = searchParams.get("error");

        // Extract user data
        const userId =
          searchParams.get("user[id]") || searchParams.get("user.id");
        const userName =
          searchParams.get("user[name]") || searchParams.get("user.name");
        const userEmail =
          searchParams.get("user[email]") || searchParams.get("user.email");
        const userRole =
          searchParams.get("user[role]") || searchParams.get("user.role");
        const userOrganizationId =
          searchParams.get("user[organization_id]") ||
          searchParams.get("user.organization_id");
        const approvalStatus =
          searchParams.get("user[approvalStatus]") ||
          searchParams.get("user.approvalStatus");

        const organization_llm_type =
          searchParams.get("user[organization_llm_type]") ||
          searchParams.get("organization_llm_type") ||
          allParams["organization_llm_type"] ||
          allParams["user[organization_llm_type]"] ||
          undefined;

        // Alternative: Check if user data is in different format
        const userData = {
          id: userId || allParams["user[id]"] || allParams["user.id"],
          name: userName || allParams["user[name]"] || allParams["user.name"],
          email:
            userEmail || allParams["user[email]"] || allParams["user.email"],
          role: userRole || allParams["user[role]"] || allParams["user.role"],
          organization_id:
            userOrganizationId ||
            allParams["user[organization_id]"] ||
            allParams["user.organization_id"] ||
            null,
          approvalStatus:
            approvalStatus ||
            allParams["user[approvalStatus]"] ||
            allParams["user.approvalStatus"],
          plan:
            allParams["user[plan]"] ||
            searchParams.get("user[plan]") ||
            searchParams.get("plan") ||
            undefined,
          trial_ends_at:
            allParams["user[trial_ends_at]"] ||
            searchParams.get("user[trial_ends_at]") ||
            searchParams.get("trial_ends_at") ||
            undefined,
          trial_approved_at:
            allParams["user[trial_approved_at]"] ||
            searchParams.get("user[trial_approved_at]") ||
            searchParams.get("trial_approved_at") ||
            undefined,
          approval_status:
            allParams["user[approval_status]"] ||
            searchParams.get("user[approval_status]") ||
            undefined,
        };

        // Allow future trial-expired OAuth responses to proceed if a token is present.
        const canProceedWithExpiredTrial =
          code === "trial_expired" && Boolean(token);
        if (success !== "true" && !canProceedWithExpiredTrial) {
          const errorToShow =
            code === "trial_expired"
              ? errorParam || "Trial has ended"
              : errorParam || "Authentication was not successful";
          throw new Error(errorToShow);
        }

        if (!token) {
          throw new Error("No token received");
        }

        if (!userData.id || !userData.email) {
          throw new Error("Incomplete user data received");
        }

        // Show success state
        setIsProcessing(false);
        setIsSuccess(true);
        setSuccessMessage("Authentication successful! Redirecting...");

        // Store user data and token (include hasAccessRequest for pending users)
        const role = userData.role || "user";
        const isOnboarded = onboardFlag === "true";
        const isPendingApproval = code === "pending_approval";
        const isRejected =
          code === "access_rejected" || approvalStatusParam === "rejected";
        const hasAccessRequestResolved = isPendingApproval && hasAccessRequest;

        const explicitApprovalFromUrl =
          (approvalStatusParam && approvalStatusParam !== ""
            ? approvalStatusParam
            : null) ||
          (userData.approval_status && userData.approval_status !== ""
            ? userData.approval_status
            : null);

        const approvalStatusResolved = isRejected
          ? "rejected"
          : isPendingApproval
            ? "pending"
            : explicitApprovalFromUrl
              ? explicitApprovalFromUrl
              : !isOnboarded
                ? null
                : "approved";

        const userForRoute = {
          id: userData.id,
          name: userData.name || userData.email.split("@")[0],
          email: userData.email,
          organization_id: userData.organization_id,
          profileImage: null,
          isOnboarded,
          role,
          approvalStatus: approvalStatusResolved,
          hasAccessRequest: hasAccessRequestResolved,
          ...(!isOnboarded ? { pricingPageCompleted: false } : {}),
          ...(requestTarget && { requestTarget }),
          ...(orgName && { orgName }),
          ...(requestSubmittedAt && { requestSubmittedAt }),
          ...(rejectedBy && { rejectedBy }),
          ...(userData.plan !== undefined &&
            userData.plan !== "" && { plan: userData.plan }),
          ...(userData.trial_approved_at !== undefined &&
            userData.trial_approved_at !== "" && {
              trial_approved_at: userData.trial_approved_at,
            }),
          ...(userData.trial_ends_at !== undefined &&
            userData.trial_ends_at !== "" && {
              trial_ends_at: userData.trial_ends_at,
            }),
          ...(organization_llm_type !== undefined &&
            organization_llm_type !== "" && {
              organization_llm_type: organization_llm_type,
            }),
        };

        setUser(userForRoute);

        localStorage.setItem("token", token);
        window.dispatchEvent(new Event("tokenUpdated"));

        showSuccess(
          isRejected
            ? "You can view your request status."
            : "Authentication successful!",
        );

        const targetPath = getTargetRoute(userForRoute);
        navigate(targetPath, { replace: true });
      } catch (err) {
        console.error("Error processing auth success:", err);
        const errorMessage =
          err.message || "Failed to process authentication. Please try again.";
        setError(errorMessage);
        showError(errorMessage);
        navigate("/signup", { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    processAuthSuccess();
  }, [searchParams, navigate, setUser]);

  // Show success state
  if (isSuccess) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div className="text-green-600 text-lg font-semibold">
            Authentication Successful!
          </div>
          <div className="text-gray-700">{successMessage}</div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isProcessing) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4">
          <Loader text="Processing authentication..." />
        </div>
      </div>
    );
  }

  // Show error state (including pending_approval and trial_expired)
  if (error) {
    const isPendingApproval = error === "pending_approval";
    const isTrialExpired = error === "trial_expired";

    const title = isPendingApproval
      ? "Account Pending Approval"
      : isTrialExpired
        ? "Trial Ended"
        : "Authentication Error";

    const message = isPendingApproval
      ? "Your account is pending approval. You will be notified when you can sign in."
      : isTrialExpired
        ? "Your trial has ended. Please contact us to continue."
        : error;

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <div className="text-red-600 text-lg font-semibold">{title}</div>
          <div className="text-gray-700">{message}</div>
          <button
            onClick={() => navigate("/signup")}
            className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-[#0d5061] transition-colors"
          >
            Return to Sign Up
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthSuccessHandler;
